import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ClientListPage } from "./pages/ClientListPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { NumbersPage } from "./pages/NumbersPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { UpgradePage } from "./pages/UpgradePage";
import { useAuth } from "./hooks/useAuth";
import { version } from "../package.json";

function FeedbackWrapper(): ReactNode {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  return <FeedbackWidget page={location.pathname} />;
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/clients" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:clientId"
              element={
                <ProtectedRoute>
                  <ClientDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:clientId/numbers"
              element={
                <ProtectedRoute>
                  <NumbersPage />
                </ProtectedRoute>
              }
            />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/dashboard" element={<Navigate to="/clients" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <FeedbackWrapper />
          <div className="version-tag">v{version}</div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
